import { useEffect } from 'react';
import { connect, ConnectedProps } from 'react-redux';

import { MessageType } from 'dashboard/lib/interfaces/Message';
import {
  QueryType,
  RelativeTimeSpan,
  VerificationChecksAnalyticsQueryParams,
  VerificationVolumeAnalyticsQueryParams,
} from 'dashboard/models/analytics-query-template';
import { ExporterFileType } from 'dashboard/models/exporter';
import { ProductFeatureName } from 'dashboard/models/organization';
import { showMessage } from 'dashboard/redux/actions/messages';
import { getAllTemplates } from 'dashboard/redux/selectors/organization';
import { RootState } from 'dashboard/redux/store';
import {
  createAnalyticsQueryTemplate,
  getAnalyticsQueryTemplates,
} from 'dashboard/redux/thunk-actions/analytics-query-templates';
import {
  loadExporterMetricsVerificationChecks,
  loadExporterMetricsVerificationVolume,
} from 'dashboard/redux/thunk-actions/metrics-verification';
import DashboardVerificationAnalyticsView from 'dashboard/views/DashboardVerificationAnalyticsView';
import useApiCallback from 'lib/hooks/useApiCallback';
import useApiErrorHandler from 'lib/hooks/useApiErrorHandler';
import useProductFeature from 'lib/hooks/useProductFeature';
import { isPermissionsError, permissionsErrorMessage } from 'lib/permissions-errors';

const DashboardVerificationAnalyticsRoute = (props: PropsFromRedux) => {
  const productFeatureIsDisabled = !useProductFeature(ProductFeatureName.VerificationsAnalytics);
  const {
    currentEnvironment,
    filteredVolumeSummary,
    lastQueryFilters,
    filters,
    volumeLastQueryFilterRules,
    checksLastQueryFilterRules,
    verificationChecksOverTime,
    templates,
    queriedAt,
  } = props;

  const handleExportData = async (
    currentUrl:
      | '/dashboard/verification-analytics/volume'
      | '/dashboard/verification-analytics/checks',
    fileType: ExporterFileType,
  ) => {
    try {
      if (currentUrl === '/dashboard/verification-analytics/volume') {
        await props.loadExporterMetricsVerificationVolume({
          currentEnvironment,
          // Handle the case tht one runs an export without running a query
          filters: lastQueryFilters || filters,
          filterRules: volumeLastQueryFilterRules,
          sortParams: {
            sort: 'created_timestamp',
            shouldSortDescending: true,
          },
          fileType: fileType,
        });
      } else {
        await props.loadExporterMetricsVerificationChecks({
          currentEnvironment,
          // Handle the case tht one runs an export without running a query
          filters: lastQueryFilters || filters,
          filterRules: checksLastQueryFilterRules,
          sortParams: {
            sort: 'created_timestamp',
            shouldSortDescending: true,
          },
          fileType: fileType,
        });
      }
      props.showMessage('You will receive an email when your export is ready.', MessageType.Notice);
    } catch {
      props.showMessage('Failed to create export, please try again.', MessageType.Failure);
    }
  };

  const {
    loading: _loadingQueryTemplates,
    error: errorQueryTemplates,
    callback: getQueryTemplates,
  } = useApiCallback(async () => {
    await props.getAnalyticsQueryTemplates();
  }, []);

  useApiErrorHandler({
    error: errorQueryTemplates,
    onError: (error) => {
      if (isPermissionsError(error)) {
        props.showMessage(permissionsErrorMessage(error), MessageType.Failure);
      } else {
        props.showMessage('Failed to load query templates.', MessageType.Failure);
      }
    },
    onNetworkError: () =>
      props.showMessage('We are experiencing network issues. Please try again.'),
  });

  const {
    loading: _loadingQueryTemplateCreate,
    error: createQueryTemplateError,
    callback: createQueryTemplate,
  } = useApiCallback(
    async (name: string, relativeTimeSpan: RelativeTimeSpan, queryType: QueryType) => {
      const commonParams = {
        type: queryType,
        'template-ids': filters.templateIds.filter((id) => !id.startsWith('itmpl')),
        'inquiry-template-ids': filters.templateIds.filter((id) => id.startsWith('itmpl')),
        'verification-types': filters.verificationTypes,
        'group-by': filters.groupBy,
      };

      /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
      const volumeParams: VerificationVolumeAnalyticsQueryParams = {
        ...commonParams,
      };

      /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
      const checksParams: VerificationChecksAnalyticsQueryParams = {
        ...commonParams,
        'check-types': filters.checkTypes,
        'check-requirements': filters.checkRequirements,
      };

      await props.createAnalyticsQueryTemplate({
        name,
        relativeTimeSpan,
        query: queryType === QueryType.VerificationsChecks ? checksParams : volumeParams,
      });
      props.showMessage('Query saved successfully.', MessageType.Success);
    },
    [filters, props.showMessage],
  );

  useApiErrorHandler({
    error: createQueryTemplateError,
    onError: (error) => {
      if (isPermissionsError(error)) {
        props.showMessage(permissionsErrorMessage(error), MessageType.Failure);
      } else {
        props.showMessage('Failed to create query template.', MessageType.Failure);
      }
    },
    onNetworkError: () =>
      props.showMessage('We are experiencing network issues. Please try again.'),
  });

  useEffect(() => {
    getQueryTemplates();
  }, []);

  return (
    <DashboardVerificationAnalyticsView
      productFeatureIsDisabled={productFeatureIsDisabled}
      /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
      handleExportData={handleExportData}
      filteredVolumeSummary={filteredVolumeSummary}
      verificationChecksOverTime={verificationChecksOverTime}
      lastQueryFilters={lastQueryFilters}
      templates={templates}
      queriedAt={queriedAt}
      /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
      createQueryTemplate={createQueryTemplate}
      filters={filters}
    />
  );
};

const mapStateToProps = (state: RootState) => ({
  currentEnvironment: state.currentEnvironment.currentEnvironment,
  filters: state.metricsVerification.filters,
  templates: getAllTemplates(state),
  lastQueryFilters: state.metricsVerification.lastQueryFilters,
  filteredVolumeSummary: state.metricsVerification.filteredVolumeSummary,
  verificationChecksOverTime: state.metricsVerification.verificationChecksOverTime,
  queriedAt: state.metricsVerification.queriedAt,
  volumeLastQueryFilterRules: state.metricsVerification.volumeLastQueryFilterRules,
  checksLastQueryFilterRules: state.metricsVerification.checksLastQueryFilterRules,
});

const mapDispatchToProps = {
  loadExporterMetricsVerificationVolume,
  loadExporterMetricsVerificationChecks,
  showMessage,
  createAnalyticsQueryTemplate,
  getAnalyticsQueryTemplates,
};

const connector = connect(mapStateToProps, mapDispatchToProps);
type PropsFromRedux = ConnectedProps<typeof connector>;

export default connector(DashboardVerificationAnalyticsRoute);
